/**
 * EmojiQuickPicker — small popover with a curated set of common emojis
 * for SMS composers. Deliberately lightweight (no external lib) — the
 * user just needs quick insert access, not a full 3000-emoji browser.
 *
 * Props
 * -----
 *   onPick(char)  — called with the chosen emoji character.
 *   testId        — optional data-testid prefix for the button and
 *                   popover. Defaults to "emoji-picker".
 */
import { useEffect, useRef, useState } from "react";
import { Smile } from "lucide-react";

const CATEGORIES = [
  {
    name: "Smileys",
    emojis: ["😀", "😃", "😄", "😁", "😊", "🙂", "😉", "😍", "🥰", "😘", "😎", "🤗", "🤔", "🙏", "😅", "😂", "🤣", "😭", "😢", "😔", "😴", "😳", "🥺", "😇", "🤩", "🥳", "😌", "🤪", "😜"],
  },
  {
    name: "Gestures",
    emojis: ["👍", "👎", "👌", "✌️", "🤞", "🤝", "👏", "🙌", "💪", "🫶", "❤️", "💙", "💚", "💛", "💜", "🧡", "🤍", "💯", "✨", "🔥", "⭐", "✅", "❌", "⚠️", "❗", "❓", "🎉", "🎂", "🎁"],
  },
  {
    name: "Health / Work",
    emojis: ["🏥", "🩺", "💊", "🚑", "🧑‍⚕️", "👩‍⚕️", "👨‍⚕️", "🏠", "📞", "📱", "💬", "📧", "📅", "🕐", "⏰", "☕", "🌟", "📝", "📋", "🚗", "🌞", "☀️", "🌧️", "❄️", "🌈", "🎯", "🚀"],
  },
];

export default function EmojiQuickPicker({ onPick, testId = "emoji-picker" }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors"
        title="Insert emoji"
        data-testid={`${testId}-toggle`}
      >
        <Smile className="w-4 h-4" />
      </button>
      {open && (
        <div
          className="absolute bottom-full mb-2 right-0 z-50 w-[260px] max-h-[280px] overflow-y-auto rounded-lg border bg-white shadow-lg p-2"
          data-testid={`${testId}-popover`}
        >
          {CATEGORIES.map((cat) => (
            <div key={cat.name} className="mb-2 last:mb-0">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 px-1 mb-1">
                {cat.name}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {cat.emojis.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => { onPick(e); setOpen(false); }}
                    className="h-8 w-8 rounded hover:bg-slate-100 flex items-center justify-center text-lg leading-none"
                    data-testid={`${testId}-emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
